import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { scheduleAPI, snapshotAPI, reservationAPI } from '../api/client';
import { useToastStore } from '../store/toastStore';
import { track } from '../utils/analytics';
import BusSeatLayout from './BusSeatLayout';
import BottomSheet from './BottomSheet';

/**
 * 정류장 이름 정규화 — "1)대화역" → "대화역", "A5)대화역" → "대화역"
 */
function normalizeStopName(name) {
  return (name || '').replace(/^[A-Z]?\d+\)/, '').trim();
}

/**
 * 현재 KST 시간 문자열 (HH:MM)
 */
function getCurrentKST() {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(11, 16);
}

export default function QuickHomeCard({ userId }) {
  const navigate = useNavigate();
  const showToast = useToastStore(s => s.showToast);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedBus, setSelectedBus] = useState(null);
  const [selectedSeat, setSelectedSeat] = useState(null);

  // 유저 스케줄 조회 (캐시 활용)
  const { data: schedules } = useQuery({
    queryKey: ['schedules', userId],
    queryFn: async () => {
      const result = await scheduleAPI.list(userId);
      return result.success ? result.data || [] : [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  // 스냅샷 조회 (캐시 활용)
  const { data: snapshot } = useQuery({
    queryKey: ['snapshot', 'exclude-thursday'],
    queryFn: async () => {
      const result = await snapshotAPI.getLatest({ excludeDay: 'thursday' });
      return result.success ? result.data : null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // 유저의 홈 정류장 파악 (하교 > 등교 순)
  const homeStop = useMemo(() => {
    if (!schedules?.length) return null;

    // 1순위: 하교 스케줄의 stop_name
    const downSchedules = schedules.filter(s => s.direction === 'DOWN');
    if (downSchedules.length > 0) {
      return normalizeStopName(downSchedules[0].stop_name);
    }

    // 2순위: 등교 스케줄의 stop_name
    const upSchedules = schedules.filter(s => s.direction === 'UP');
    if (upSchedules.length > 0) {
      return normalizeStopName(upSchedules[0].stop_name);
    }

    return null;
  }, [schedules]);

  // 매칭 하교 노선에서 가장 빠른 버스 찾기
  const nextBus = useMemo(() => {
    if (!homeStop || !snapshot?.routes_data?.DOWN) return null;

    const nowTime = getCurrentKST();
    const candidates = [];

    for (const route of snapshot.routes_data.DOWN) {
      const stops = route.stopList || [];
      const matchedStop = stops.find(s => normalizeStopName(s.stopName) === homeStop);
      if (!matchedStop) continue;

      const buses = route.busList || [];
      for (const bus of buses) {
        if (bus.operateTime > nowTime) {
          candidates.push({
            routeName: route.groupName,
            lineGroupSeq: route.lineGroupSeq,
            busTime: bus.operateTime,
            busSeq: bus.busSeq,
            stopName: matchedStop.stopName,
            seatCount: bus.seatCount,
            appCount: bus.appCount,
          });
        }
      }
    }

    if (candidates.length === 0) return null;

    // 가장 빠른 버스
    candidates.sort((a, b) => a.busTime.localeCompare(b.busTime));
    return candidates[0];
  }, [homeStop, snapshot]);

  // 출발까지 남은 시간 계산
  const timeRemaining = useMemo(() => {
    if (!nextBus) return '';
    const now = getCurrentKST();
    const [nowH, nowM] = now.split(':').map(Number);
    const [busH, busM] = nextBus.busTime.split(':').map(Number);
    const diffMin = (busH * 60 + busM) - (nowH * 60 + nowM);
    if (diffMin <= 0) return '곧 출발';
    if (diffMin < 60) return `${diffMin}분 후 출발`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return m > 0 ? `${h}시간 ${m}분 후 출발` : `${h}시간 후 출발`;
  }, [nextBus]);

  // 카드용 실시간 잔여석 (timetable API에서 appCount/readyCount 사용, 30초 갱신)
  const { data: cardBusInfo } = useQuery({
    queryKey: ['timetable-card', nextBus?.lineGroupSeq],
    queryFn: async () => {
      const res = await reservationAPI.getTimetable(userId, 'DOWN', nextBus.lineGroupSeq);
      if (res.success && res.data?.timetable) {
        const realBus = res.data.timetable.find(b => b.time === nextBus.busTime);
        if (realBus) return realBus;
      }
      return null;
    },
    enabled: !!nextBus?.lineGroupSeq && !!userId,
    refetchInterval: 30000,
    staleTime: 15000,
  });

  // 바텀시트용 좌석 조회 (3초 간격)
  const { data: seatsData, isLoading: loadingSeats } = useQuery({
    queryKey: ['seats-quick', selectedBus?.busSeq],
    queryFn: async () => {
      const result = await reservationAPI.getSeats(userId, selectedBus.busSeq);
      if (result.success) {
        const d = result.data;
        if (Array.isArray(d)) return d;
        if (d?.seats && Array.isArray(d.seats)) return d.seats;
        if (d?.seatList && Array.isArray(d.seatList)) return d.seatList;
        return [];
      }
      throw new Error(result.error);
    },
    enabled: !!selectedBus?.busSeq && sheetOpen,
    refetchInterval: 3000,
  });

  // 예약 실행
  const reserveMutation = useMutation({
    mutationFn: async () => {
      const result = await reservationAPI.execute({
        userId,
        busSeq: selectedBus.busSeq,
        seatNo: selectedSeat,
        stopSeq: selectedBus.realStopSeq || selectedBus.stopName,
      });
      if (!result.success && result.result !== 'OK') {
        throw new Error(result.error || result.msg || '예약에 실패했습니다');
      }
      return result;
    },
    onSuccess: () => {
      track('reservation-executed', { routeName: selectedBus.routeName, seatNo: selectedSeat });
      showToast(`${selectedSeat}번 좌석 예약 완료!`, 'success');
      setSheetOpen(false);
      setSelectedBus(null);
      setSelectedSeat(null);
    },
    onError: (err) => {
      showToast(err.message || '예약에 실패했습니다', 'error');
    },
  });

  // 실시간 busSeq 조회 후 바텀시트 열기
  const [loadingRealtime, setLoadingRealtime] = useState(false);

  const handleCardTap = async () => {
    if (!nextBus) return;
    track('quick-home-click');
    setSelectedSeat(null);
    setSheetOpen(true);
    setLoadingRealtime(true);

    try {
      // 실시간 timetable에서 정확한 busSeq + stopSeq 가져오기
      const res = await reservationAPI.getTimetable(userId, 'DOWN', nextBus.lineGroupSeq);
      if (res.success && res.data?.timetable) {
        const realBus = res.data.timetable.find(b => b.time === nextBus.busTime);
        // 실시간 stops에서 유저 정류장의 stopSeq 찾기
        const stops = res.data.stops || [];
        const userStop = stops.find(s => normalizeStopName(s.stopName || s.name || '') === homeStop);
        if (realBus) {
          setSelectedBus({
            ...nextBus,
            busSeq: realBus.seq,
            realStopSeq: userStop?.seq || userStop?.stopSeq || null,
          });
          setLoadingRealtime(false);
          return;
        }
      }
    } catch (e) {
      console.error('[QuickHome] Realtime timetable fetch failed:', e);
    }

    // fallback: 스냅샷 busSeq 사용
    setSelectedBus(nextBus);
    setLoadingRealtime(false);
  };

  const handleSeatClick = useCallback((seatNo) => {
    setSelectedSeat(prev => prev === seatNo ? null : seatNo);
  }, []);

  // 스케줄이 없거나 홈 정류장을 못 찾으면 기존 카드 표시
  if (!homeStop) {
    return (
      <div
        onClick={() => navigate('/reserve')}
        className="bg-gradient-to-r from-tayo to-tayo-dark rounded-2xl p-6 mb-5 cursor-pointer btn-press animate-fade-up stagger-1 hover:shadow-lg transition-shadow"
      >
        <div className="text-white">
          <p className="text-xl font-bold">첫 예약을 해보세요!</p>
          <p className="text-sm text-white/70 mt-2">탭하여 버스 예약하기 &rarr;</p>
        </div>
      </div>
    );
  }

  // 남은 하교 버스가 없으면
  if (!nextBus) {
    return (
      <div className="bg-gradient-to-r from-gray-600 to-gray-700 rounded-2xl p-6 mb-5 animate-fade-up stagger-1">
        <div className="text-white">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
            </svg>
            <p className="text-sm font-medium text-white/70">바로 집가기</p>
          </div>
          <p className="text-base font-bold">오늘 남은 하교 버스가 없어요</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 바로 집가기 카드 */}
      <div
        onClick={handleCardTap}
        className="bg-gradient-to-r from-tayo to-tayo-dark rounded-2xl p-6 mb-5 cursor-pointer btn-press animate-fade-up stagger-1 hover:shadow-lg transition-shadow"
      >
        <div className="text-white">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
              </svg>
              <p className="text-sm font-medium text-white/70">바로 집가기</p>
            </div>
            <span className="text-xs bg-white/20 px-2.5 py-1 rounded-full font-medium">
              {timeRemaining}
            </span>
          </div>
          <p className="text-xl font-bold">{nextBus.routeName} {nextBus.busTime}</p>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-white/70">{normalizeStopName(nextBus.stopName)} 방면</p>
            {cardBusInfo && (() => {
              const remaining = cardBusInfo.seatCount - cardBusInfo.appCount;
              const waiting = cardBusInfo.readyCount || 0;
              return (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  remaining === 0
                    ? 'bg-red-500/30 text-red-100'
                    : remaining <= 5
                    ? 'bg-amber-500/30 text-amber-100'
                    : 'bg-emerald-500/30 text-emerald-100'
                }`}>
                  {remaining === 0
                    ? `만석${waiting > 0 ? ` · 대기 ${waiting}명` : ''}`
                    : `잔여 ${remaining}석`}
                </span>
              );
            })()}
          </div>
          <p className="text-xs text-white/50 mt-1">탭하여 좌석 선택 &rarr;</p>
        </div>
      </div>

      {/* 좌석 선택 바텀시트 */}
      <BottomSheet isOpen={sheetOpen && !!selectedBus} onClose={() => setSheetOpen(false)}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-3">
          <div>
            <h3 className="text-lg font-bold text-charcoal">{selectedBus?.routeName}</h3>
            <p className="text-sm text-sub-text">{selectedBus?.busTime} · {normalizeStopName(selectedBus?.stopName)} 방면</p>
          </div>
          <button
            onClick={() => setSheetOpen(false)}
            className="p-1.5 text-sub-text hover:text-charcoal hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 pb-6">
          {loadingSeats ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-tayo border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <BusSeatLayout
                mode="reserve"
                seats={(Array.isArray(seatsData) ? seatsData : []).map(s => ({
                  seatNo: Number(s.seatNo),
                  available: s.isReserved === 'NO',
                  reserved: s.isReserved !== 'NO',
                }))}
                selectedSeats={selectedSeat ? [selectedSeat] : []}
                onSeatClick={handleSeatClick}
              />

              <button
                type="button"
                disabled={!selectedSeat || reserveMutation.isPending}
                onClick={() => reserveMutation.mutate()}
                className="w-full mt-5 py-4 rounded-xl text-white font-bold text-base transition-all duration-200 btn-press bg-gradient-to-r from-tayo to-tayo-dark disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed shadow-lg shadow-tayo/20 disabled:shadow-none"
              >
                {reserveMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    예약 중...
                  </span>
                ) : selectedSeat ? `${selectedSeat}번 좌석 예약하기` : '좌석을 선택하세요'}
              </button>
            </>
          )}
        </div>
      </BottomSheet>
    </>
  );
}
